import { useCallback, useMemo, useRef, useState } from 'react';
import { Node, Edge } from 'reactflow';
import { FlowDSL } from '@holloway/dsl';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { flowsApi } from '@/services/flows.api';
import { Variable } from '@/services/variables.api';
import { toVariableDefs } from '@/features/editor/utils/variables';
import { makeTrailingSave, TrailingSaveScheduler } from '@/features/editor/utils/trailingSave';
import { stableHash } from '@/features/editor/utils/stableHash';

/**
 * Mirrors JSON_BODY_LIMIT_BYTES in apps/api/src/bootstrap/body-parser.ts. The
 * two are separate packages, so this is a deliberate copy — raising the server
 * limit without raising this one only makes the warning appear early, never
 * late, which is the safe direction for the copy to drift.
 */
const SAVE_LIMIT_BYTES = 5 * 1024 * 1024;

/** Start showing size once a flow is halfway to the limit. */
const SAVE_SIZE_VISIBLE_BYTES = SAVE_LIMIT_BYTES / 2;

/**
 * A 413 means the flow itself is too big to send, so retrying is pointless —
 * the message has to tell the author what to change. Axios only ever reports
 * "Request failed with status code 413", which reads like a transient glitch.
 */
function describeSaveError(error: any): string {
  if (error?.response?.status === 413) {
    return 'This flow is too large to save. Shorten the text on its longest pages, or split it into separate flows, then save again.';
  }
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return 'The API did not respond in time. Check the local API and try saving again.';
  }
  return error?.message || 'Something went wrong while saving this flow.';
}

interface UseFlowPersistenceProps {
  flowId: string;
  currentVersionId?: string;
  nodes: Node[];
  edges: Edge[];
  variables: Variable[];
  resetUnsavedChanges: (explicitHash?: string) => void;
}

export function useFlowPersistence({
  flowId,
  currentVersionId,
  nodes,
  edges,
  variables,
  resetUnsavedChanges,
}: UseFlowPersistenceProps) {
  const queryClient = useQueryClient();
  const [lastSavedBytes, setLastSavedBytes] = useState<number | null>(null);

  /**
   * Size of the flow as of the last save attempt, surfaced only once it is
   * worth an author's attention. A flow only grows, and the save that finally
   * crosses the server limit leaves it unsaveable — so the number needs to be
   * visible before that, not explained afterwards.
   */
  const saveSize = useMemo(() => {
    if (lastSavedBytes === null || lastSavedBytes < SAVE_SIZE_VISIBLE_BYTES) return null;
    return {
      bytes: lastSavedBytes,
      megabytes: (lastSavedBytes / (1024 * 1024)).toFixed(1),
      percentOfLimit: Math.round((lastSavedBytes / SAVE_LIMIT_BYTES) * 100),
      // Past 90% the next few edits are likely to fail, matching the threshold
      // the API warns on.
      critical: lastSavedBytes >= SAVE_LIMIT_BYTES * 0.9,
    };
  }, [lastSavedBytes]);

  const getCurrentFlowDSL = useCallback((): FlowDSL => {
    // Convert ReactFlow nodes back to DSL nodes
    const nodesWithConnections = nodes
      .filter((node) => node.type !== 'branchStub') // Exclude stub nodes
      .map((node) => {
        const nodeData = { ...node.data };
        const outgoingEdges = edges.filter((e) => e.source === node.id);

        // Handle different node types
        if (nodeData.type === 'If') {
          const trueEdge = outgoingEdges.find((e) => e.label === 'true' || e.sourceHandle === 'true');
          const falseEdge = outgoingEdges.find((e) => e.label === 'false' || e.sourceHandle === 'false');
          if (trueEdge) {
            nodeData.then = trueEdge.target;
          } else {
            delete nodeData.then;
          }
          if (falseEdge) {
            nodeData.else = falseEdge.target;
          } else {
            delete nodeData.else;
          }
        } else if (nodeData.type === 'Branch') {
          // Branch node has multiple labeled edges
          const branches = outgoingEdges.map((edge) => ({
            label: edge.label || '',
            target: edge.target,
          }));
          nodeData.branches = branches;
        } else if (nodeData.type === 'Switch') {
          // Switch node with cases
          const caseEdges = outgoingEdges.filter((e) => e.label && typeof e.label === 'string' && e.label.startsWith('case'));
          nodeData.cases = caseEdges.map((edge) => ({
            // In a real implementation, you'd store the condition somewhere
            condition: { path: '', operator: '==' as const, value: '' },
            next: edge.target,
          }));
          const defaultEdge = outgoingEdges.find((e) => e.label === 'default');
          if (defaultEdge) {
            nodeData.defaultNext = defaultEdge.target;
          }
        } else if (nodeData.type === 'Page') {
          // Check if Page has branching elements
          const branchingElement = (nodeData.elements || []).find((el: any) => el.createBranches === true);

          if (branchingElement) {
            // Page has branching - don't set 'next', branches are in element properties
            delete nodeData.next;
          } else {
            // Normal page - use first edge as 'next'
            const firstEdge = outgoingEdges[0];
            if (firstEdge) {
              nodeData.next = firstEdge.target;
            } else {
              delete nodeData.next;
            }
          }
        } else {
          // Most nodes have a simple 'next' property
          const firstEdge = outgoingEdges[0];
          if (firstEdge) {
            nodeData.next = firstEdge.target;
          } else {
            // Remove next property if no outgoing edges
            delete nodeData.next;
          }
        }

        // Remove ReactFlow-specific properties and add position
        delete nodeData.color;
        return {
          ...nodeData,
          position: node.position,
        };
      });

    return {
      schemaVersion: '1.0',
      id: flowId,
      name: 'Flow',
      // Embed the GUID-keyed variable definitions (source of truth: DB table)
      // so the runtime receives them.
      variables: toVariableDefs(variables),
      nodes: nodesWithConnections,
    };
  }, [flowId, nodes, edges, variables]);

  // ── Latest-value refs ────────────────────────────────────────────────────
  //
  // useCallback closes over its deps at the time React creates the callback.
  // If saveFlowBody called getCurrentFlowDSL / resetUnsavedChanges / currentVersionId
  // directly, it would capture a stale snapshot.  Instead we update these refs
  // on every render so the trailing run always reads the latest state —
  // including any edits made while the first save was in flight.
  const getCurrentFlowDSLRef = useRef(getCurrentFlowDSL);
  const resetUnsavedChangesRef = useRef(resetUnsavedChanges);
  const currentVersionIdRef = useRef(currentVersionId);
  // A ref whose .current() returns the stableHash of the CURRENT nodes/edges.
  // saveFlowBody calls this immediately before the PUT so the hash reflects
  // exactly the state that was sent — not the state at save completion time
  // (which may already contain newer canvas edits).
  const getHashRef = useRef(() => stableHash(nodes, edges));

  getCurrentFlowDSLRef.current = getCurrentFlowDSL;
  resetUnsavedChangesRef.current = resetUnsavedChanges;
  currentVersionIdRef.current = currentVersionId;
  getHashRef.current = () => stableHash(nodes, edges);

  // ── Single-flight scheduler ──────────────────────────────────────────────
  //
  // Replaces the old `isSavingRef` guard, which silently dropped any save
  // requested while another was in flight.  The scheduler instead queues
  // exactly one trailing run that fires after the in-flight save settles
  // (success or failure), capturing the freshest editor state via the refs.
  //
  // Lazy init: useRef(makeTrailingSave()) calls makeTrailingSave() on every
  // render (React evaluates the argument even though it ignores it after the
  // first render).  The explicit null-check ensures the factory runs once.
  const schedulerRef = useRef<TrailingSaveScheduler | null>(null);
  if (schedulerRef.current === null) schedulerRef.current = makeTrailingSave();

  // ── Save body ────────────────────────────────────────────────────────────
  //
  // Reads getDSL / resetUnsaved / currentVersionId through refs so it always
  // uses up-to-date values even when called from a trailing run.
  // Stable: only depends on flowId and queryClient (both constant per flow).
  const saveFlowBody = useCallback(async () => {
    try {
      const flowData = getCurrentFlowDSLRef.current();
      // Capture the stableHash of the state we are about to PUT.  We must
      // snapshot this BEFORE the await so it reflects the nodes/edges that
      // went into `flowData`, not any canvas edits the author makes while the
      // network request is in flight.
      const sentHash = getHashRef.current();

      // Measured here rather than on every edit: sizing a multi-megabyte flow
      // means serialising it, which is wasted work on each keystroke when the
      // number only needs to be right as of the last save.
      setLastSavedBytes(new TextEncoder().encode(JSON.stringify(flowData)).length);

      const versionId = currentVersionIdRef.current;
      if (versionId) {
        await flowsApi.updateVersion(flowId, versionId, flowData);
      } else {
        await flowsApi.createVersion(flowId, flowData);
      }

      // Only mark clean and refresh the cache when no trailing save is pending.
      //
      // If another save is queued we must NOT invalidate the query: doing so
      // causes the flow to be re-loaded from the server, which overwrites the
      // in-memory edits the trailing save still needs to send.  Both actions
      // happen together after the last save in the chain, so the "Saved" label
      // appears only once all queued edits have actually reached the server.
      if (!schedulerRef.current!.hasPending) {
        // Pass the pre-PUT hash so that any canvas edit made while this save
        // was in flight is NOT falsely marked as saved.  Those edits will
        // produce a hash different from sentHash, keeping hasUnsavedChanges
        // true and the toolbar showing "Save*".
        resetUnsavedChangesRef.current(sentHash);
        // Invalidate so navigating away and back loads the saved version.
        // The loading effect in useFlowEditorState guards against this refetch
        // overwriting in-memory canvas edits (hasUnsavedChanges check).
        queryClient.invalidateQueries({ queryKey: ['flow', flowId] });
      }
    } catch (error: any) {
      console.error('Failed to save flow:', error);
      notifications.show({
        color: 'red',
        title: 'Flow was not saved',
        message: describeSaveError(error),
        // A size problem needs reading and acting on, so it should not vanish
        // on the usual timer the way a retryable blip can.
        autoClose: error?.response?.status === 413 ? false : undefined,
      });
    }
  }, [flowId, queryClient]);

  /** Schedule a save.  Returns immediately; at most one save runs at a time,
   *  and a save requested while one is in flight queues a single trailing run
   *  that fires after the in-flight save settles. */
  const saveFlow = useCallback(() => {
    schedulerRef.current!.schedule(saveFlowBody);
  }, [saveFlowBody]);

  const createNewVersion = useCallback(async (comment?: string) => {
    try {
      // Wait until the trailing-save scheduler is idle before reading the DSL.
      // Without this, a trailing save in flight could PUT to the old version
      // after the new one is created, overwriting the new version's content.
      await schedulerRef.current!.idle();

      const flowData = getCurrentFlowDSL();
      await flowsApi.createVersion(flowId, flowData, comment);
      resetUnsavedChanges();
      queryClient.invalidateQueries({ queryKey: ['flow', flowId] });

      // Reload page to get new version
      window.location.reload();
    } catch (error: any) {
      console.error('Failed to create new version:', error);
      notifications.show({
        color: 'red',
        title: 'New version was not created',
        message: describeSaveError(error),
        autoClose: error?.response?.status === 413 ? false : undefined,
      });
    }
  }, [flowId, getCurrentFlowDSL, resetUnsavedChanges, queryClient]);

  const exportJSON = useCallback(() => {
    const flowData = getCurrentFlowDSL();
    const blob = new Blob([JSON.stringify(flowData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flow-${flowId}.json`;
    a.click();
  }, [flowId, getCurrentFlowDSL]);

  return {
    saveFlow,
    createNewVersion,
    exportJSON,
    getCurrentFlowDSL,
    saveSize,
  };
}
